import type { GameState, Pile } from '../../engine/game-state.js';
import { createGrid, CLASSIC_LAYOUT } from '../../render/classic/layout.js';
import type { ClassicSpec } from '../../render/classic/types.js';
import { CARD_W, CARD_H } from '../../render/classic/types.js';
import { drawFromStock, klondikeRules } from './rules.js';

const TABLEAU_IDS = ['t1', 't2', 't3', 't4', 't5', 't6', 't7'] as const;
const FOUNDATION_IDS = ['f-spades', 'f-hearts', 'f-diamonds', 'f-clubs'] as const;

// 7-column grid. Top row: stock, waste, gap, 4 foundations.
// Bottom row: 7 fan-down tableau columns.
const COLS = 7;
const TOP_Y = 24;
const TABLEAU_Y = TOP_Y + CARD_H + CLASSIC_LAYOUT.ROW_GAP;
const FELT_W = COLS * CARD_W + (COLS - 1) * CLASSIC_LAYOUT.COL_GAP + 48;
// Worst-case tableau column: 6 face-down + ~13 face-up = ~19 cards fanned.
const FELT_H = TABLEAU_Y + CARD_H + 19 * CLASSIC_LAYOUT.FAN_DOWN + 24;

// Draw-3 fans the last 3 cards of the waste pile to the right by this amount.
const WASTE_FAN = 14;

const layout = createGrid({
  cols: COLS,
  feltWidth: FELT_W,
  rows: [
    { y: TOP_Y, slots: ['stock', 'waste', null, ...FOUNDATION_IDS] },
    { y: TABLEAU_Y, fanDown: true, slots: [...TABLEAU_IDS] },
  ],
});

function dragStackIds(state: GameState, cardId: string): string[] {
  for (const pile of Object.values(state.piles)) {
    const idx = pile.cards.findIndex((c) => c.id === cardId);
    if (idx === -1) continue;
    if (pile.type === 'stock') return [];
    if (pile.type !== 'tableau') {
      // waste/foundation: only the top card is grabbable
      if (idx !== pile.cards.length - 1) return [];
      return [cardId];
    }
    // tableau: validate alt-color descending so we don't drag garbage. The
    // engine re-validates at drop, but checking here means the user only
    // sees a "drag accepted" lift on legal sequences.
    const seq = pile.cards.slice(idx);
    if (!seq[0]!.faceUp) return [];
    for (let i = 1; i < seq.length; i++) {
      const a = seq[i - 1]!;
      const b = seq[i]!;
      if (!b.faceUp) return [];
      if (a.rank !== b.rank + 1) return [];
    }
    return seq.map((c) => c.id);
  }
  return [];
}

// Auto-send order: foundations first, then tableau columns. Used both for
// double-click (built into classic-app) and single-click (handled below).
const AUTO_SEND_ORDER = [...FOUNDATION_IDS, ...TABLEAU_IDS];

function tryAutoSendInPlace(state: GameState, fromPile: Pile, cardId: string): boolean {
  // Only the top face-up card auto-sends — match vanilla Klondike.
  const top = fromPile.cards[fromPile.cards.length - 1];
  if (!top || top.id !== cardId || !top.faceUp) return false;
  for (const toId of AUTO_SEND_ORDER) {
    if (toId === fromPile.id) continue;
    const toPile = state.piles[toId];
    if (!toPile) continue;
    const move = { from: fromPile.id, to: toId, cardId };
    if (!klondikeRules.canMove(state, move)) continue;
    const idx = fromPile.cards.findIndex((c) => c.id === cardId);
    const moving = fromPile.cards.splice(idx);
    toPile.cards.push(...moving);
    state.movesCount++;
    klondikeRules.afterMove?.(state, move);
    return true;
  }
  return false;
}

export interface KlondikeClassicOptions {
  draw?: 1 | 3;
}

export function makeKlondikeClassicSpec(opts: KlondikeClassicOptions = {}): ClassicSpec {
  const draw: 1 | 3 = opts.draw === 3 ? 3 : 1;
  return {
    rules: klondikeRules,
    feltWidth: FELT_W,
    feltHeight: FELT_H,
    slotForPile: (id) => layout.slot(id),
    dropExclude: () => ['stock'],
    dragStackIds,
    autoSendOrder: () => AUTO_SEND_ORDER,
    cardOffset(pile, indexInPile) {
      // Draw-3: fan the last 3 waste cards rightward so the player can see
      // the deal. Draw-1 stacks them on top of each other.
      if (pile.id !== 'waste' || draw !== 3) return { dx: 0, dy: 0 };
      const last = pile.cards.length - 1;
      const visible = Math.max(0, indexInPile - (last - 2));
      return { dx: visible * WASTE_FAN, dy: 0 };
    },
    onPileClick(state, pile, cardId) {
      // Stock click: deal N cards or recycle waste. Triggered both on a
      // face-down stock-card click and on a click of the empty stock slot
      // (classic-app dispatches with pile.id as cardId in the empty case).
      if (pile.id === 'stock') {
        drawFromStock(state, draw);
        state.movesCount++;
        return true;
      }
      // Anywhere else: try to auto-send the top card. Single-click parity
      // with the original 8-bit Klondike (and Microsoft Solitaire).
      return tryAutoSendInPlace(state, pile, cardId);
    },
    statusText() { return null; },
  };
}

export const klondikeClassicSpec: ClassicSpec = makeKlondikeClassicSpec({ draw: 1 });

void CARD_W; void CARD_H;
